import { useEffect, useMemo, useState } from 'react';
import { Ban, CheckCircle2, Clock3, Mail, RefreshCw, Send, TestTube2, Users } from 'lucide-react';
import { api, dateTime } from '../lib/api';

const post = body => ({ method:'POST', headers:{'content-type':'application/json'}, body:JSON.stringify(body) });
const purposeLabels = {
  general:'Service invitation',
  private_preview:'Private preview',
  product_testing:'Product testing',
  early_access:'Early access',
};
const stateLabels = {
  available:'Available',
  coming_soon:'Coming soon',
  in_development:'In development',
};
const statusLabels = {
  sent:'Sent',
  accepted:'Accepted',
  delivery_failed:'Delivery failed',
  cancelled:'Cancelled',
  expired:'Expired',
};

export default function ServiceInvitations(){
  const params = new URLSearchParams(window.location.search);
  const initialCustomer = params.get('customer') || '';
  const initialService = (params.get('service') || '').trim();
  const requestedPurpose = (params.get('purpose') || '').trim();
  const initialPurpose = ['general','private_preview','product_testing','early_access'].includes(requestedPurpose) ? requestedPurpose : 'general';
  const [services,setServices] = useState([]);
  const [invitations,setInvitations] = useState([]);
  const [loading,setLoading] = useState(true);
  const [sending,setSending] = useState(false);
  const [notice,setNotice] = useState('');
  const [error,setError] = useState('');
  const [form,setForm] = useState({
    customerReference:initialCustomer,
    recipientName:'',
    recipientEmail:'',
    purpose:initialPurpose,
    expiresAt:'',
    personalMessage:'',
    services:[],
  });

  const load = async()=>{
    setLoading(true);
    try{
      const data = await api('/api/service-invitations'+(initialCustomer?'?customer='+encodeURIComponent(initialCustomer):''));
      const availableServices = data.services || [];
      setServices(availableServices);
      if (initialService) {
        const selected = availableServices.find(service => service.code === initialService || service.slug === initialService);
        if (selected) setForm(current => current.services.length ? current : { ...current, services:[selected.code] });
      }
      setInvitations(data.invitations || []);
      setError('');
    }catch(e){
      setError(e.message);
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{ void load(); },[]);

  useEffect(()=>{
    if(!initialCustomer) return;
    let active=true;
    api('/api/customers/'+encodeURIComponent(initialCustomer))
      .then(data=>{
        if(!active) return;
        const customer=data.customer || data;
        setForm(current=>({
          ...current,
          customerReference:initialCustomer,
          recipientName:customer.display_name || current.recipientName,
          recipientEmail:customer.verified_email || current.recipientEmail,
        }));
      })
      .catch(()=>{});
    return()=>{active=false};
  },[initialCustomer]);

  const selectedServices = useMemo(()=>services.filter(service=>form.services.includes(service.code)),[services,form.services]);
  const selectedPrelaunch = selectedServices.some(service=>service.state!=='available');
  const canSend = form.recipientEmail.trim() && form.services.length>0 && !(form.purpose==='general' && selectedPrelaunch) && !sending;

  const update=(key,value)=>setForm(current=>({...current,[key]:value}));
  const toggleService=code=>setForm(current=>({
    ...current,
    services:current.services.includes(code)?current.services.filter(item=>item!==code):[...current.services,code],
  }));

  const submit=async event=>{
    event.preventDefault();
    if(!canSend) return;
    setSending(true);setError('');setNotice('');
    try{
      const body = {
        customerReference:form.customerReference.trim() || null,
        recipientName:form.recipientName.trim(),
        recipientEmail:form.recipientEmail.trim(),
        purpose:form.purpose,
        expiresAt:form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
        personalMessage:form.personalMessage.trim(),
        services:form.services,
      };
      const result = await api('/api/service-invitations',post(body));
      setNotice('Invitation '+result.invitation.invitation_reference+' sent to '+result.invitation.recipient_email+'.');
      setForm(current=>({...current,personalMessage:'',expiresAt:'',services:[]}));
      await load();
    }catch(e){
      setError(e.message);
    }finally{
      setSending(false);
    }
  };

  const act=async(invitation,kind)=>{
    const prompt = kind==='cancel'
      ? invitation.status==='accepted'
        ? 'Revoke this accepted invitation access? The audit record will remain.'
        : 'Cancel this invitation? The audit record will remain.'
      : 'Resend this invitation to '+invitation.recipient_email+'?';
    if(!window.confirm(prompt)) return;
    try{
      await api('/api/service-invitations/'+encodeURIComponent(invitation.id)+'/'+kind,post({}));
      setNotice(kind==='cancel'?'Invitation cancelled.':'Invitation resent.');
      setError('');
      await load();
    }catch(e){setError(e.message)}
  };

  const metrics = useMemo(()=>({
    total:invitations.length,
    sent:invitations.filter(item=>item.status==='sent').length,
    testing:invitations.filter(item=>item.purpose==='product_testing').length,
    failed:invitations.filter(item=>item.status==='delivery_failed').length,
  }),[invitations]);

  return <div className="service-invitations-page">
    <header className="service-invitations-hero">
      <div>
        <span className="page-eyebrow">CUSTOMER OPERATIONS</span>
        <h1 className="govuk-heading-xl">Service Invitations</h1>
        <p className="govuk-body">Invite customers and selected contacts to Sousa Murray Profiles or Sousa Murray Mini Websites. Invitations are recorded against Customer Operations and do not create paid plans or subscriptions.</p>
      </div>
      <button className="button secondary govuk-button govuk-button--secondary" type="button" onClick={()=>void load()} disabled={loading}><RefreshCw size={16}/> Refresh</button>
    </header>

    <section className="service-invitation-metrics">
      <article><Users size={19}/><div><strong>{metrics.total}</strong><span>Invitation records</span></div></article>
      <article><Send size={19}/><div><strong>{metrics.sent}</strong><span>Currently sent</span></div></article>
      <article><TestTube2 size={19}/><div><strong>{metrics.testing}</strong><span>Product testing</span></div></article>
      <article><Mail size={19}/><div><strong>{metrics.failed}</strong><span>Delivery failures</span></div></article>
    </section>

    {(notice||error) && <div className={'service-invitation-notice '+(error?'error':'success')}>{error||notice}</div>}

    <div className="service-invitation-layout">
      <form className="service-invitation-composer" onSubmit={submit}>
        <div className="service-invitation-section-heading">
          <div><span>NEW INVITATION</span><h2 className="govuk-heading-m">Choose who and what to invite.</h2></div>
          <Send size={20}/>
        </div>

        <div className="service-invitation-fields">
          <label className="govuk-label"><span>Customer reference <small>optional</small></span><input className="govuk-input" value={form.customerReference} onChange={e=>update('customerReference',e.target.value)} placeholder="Customer ID or customer number"/></label>
          <label className="govuk-label"><span>Recipient name</span><input className="govuk-input" value={form.recipientName} onChange={e=>update('recipientName',e.target.value)} placeholder="Name"/></label>
          <label className="wide govuk-label"><span>Email address</span><input className="govuk-input" type="email" required value={form.recipientEmail} onChange={e=>update('recipientEmail',e.target.value)} placeholder="name@example.com"/></label>
          <label className="govuk-label"><span>Invitation type</span><select className="govuk-select" value={form.purpose} onChange={e=>update('purpose',e.target.value)}>
            <option value="general">Service invitation</option>
            <option value="private_preview">Private preview</option>
            <option value="product_testing">Product testing</option>
            <option value="early_access">Early access</option>
          </select></label>
          <label className="govuk-label"><span>Expires <small>optional</small></span><input className="govuk-input" type="datetime-local" value={form.expiresAt} onChange={e=>update('expiresAt',e.target.value)}/></label>
        </div>

        <div className="service-picker-heading"><div><strong>Select services</strong><span>You can include more than one service in the same invitation.</span></div><span>{form.services.length} selected</span></div>
        <div className="service-picker-grid">
          {services.map(service=>{
            const selected=form.services.includes(service.code);
            return <button className={'service-picker-card '+(selected?'selected':'')} type="button" onClick={()=>toggleService(service.code)} key={service.code} aria-pressed={selected}>
              <span className={'service-state '+service.state}>{stateLabels[service.state] || service.state}</span>
              <strong>{service.name}</strong>
              <small>{service.state==='available'?'Ready for normal invitations.':'Use for controlled preview, testing or early access.'}</small>
              <span className="service-picker-check">{selected?<CheckCircle2 size={18}/>:<span/>}</span>
            </button>;
          })}
        </div>

        {selectedPrelaunch && form.purpose==='general' && <div className="service-prelaunch-warning"><Clock3 size={18}/><span>One or more selected services are not generally available. Choose Private preview, Product testing or Early access before sending.</span></div>}

        <label className="service-message govuk-label"><span>Personal message <small>optional</small></span><textarea className="govuk-textarea" rows="5" maxLength="2000" value={form.personalMessage} onChange={e=>update('personalMessage',e.target.value)} placeholder="Add context, testing instructions or a short personal message for the recipient."/></label>

        <div className="service-invitation-sendbar">
          <p className="govuk-body">The invitation email lists the selected Studio services and explains its purpose. Sending an invitation does not create an account entitlement or a paid subscription.</p>
          <button className="button primary govuk-button" type="submit" disabled={!canSend}>{sending?'Sending invitation…':<><Send size={16}/> Send invitation</>}</button>
        </div>
      </form>

      <aside className="service-invitation-guidance">
        <span>HOW TO USE IT</span>
        <h2 className="govuk-heading-m">One invitation record, selected services.</h2>
        <div className="guidance-step"><b>1</b><p className="govuk-body"><strong>Choose the person.</strong><br/>Link an existing customer or invite an external contact by email. If the email uniquely matches an existing customer, Customer Operations links the invitation to that Customer360 record automatically.</p></div>
        <div className="guidance-step"><b>2</b><p className="govuk-body"><strong>Select services.</strong><br/>Invite to one service or several in the same communication.</p></div>
        <div className="guidance-step"><b>3</b><p className="govuk-body"><strong>Set the purpose.</strong><br/>Use Product testing or Private preview for controlled Studio trials, or a general invitation for publicly available services.</p></div>
        <div className="guidance-step"><b>4</b><p className="govuk-body"><strong>Keep the record.</strong><br/>Resends and cancellations remain attached to the original invitation record.</p></div>
      </aside>
    </div>

    <section className="service-invitation-register">
      <div className="service-invitation-section-heading">
        <div><span>INVITATION REGISTER</span><h2 className="govuk-heading-m">Recent invitations</h2></div>
        <span>{loading?'Loading…':invitations.length+' records'}</span>
      </div>
      {!loading && !invitations.length ? <div className="empty-invitations"><Mail size={28}/><strong>No service invitations yet.</strong><span>Your first invitations will appear here after they are sent.</span></div> :
      <div className="service-invitation-table-wrap"><table className="service-invitation-table govuk-table">
        <thead className="govuk-table__head"><tr className="govuk-table__row"><th className="govuk-table__header">Invitation</th><th className="govuk-table__header">Recipient</th><th className="govuk-table__header">Services</th><th className="govuk-table__header">Purpose</th><th className="govuk-table__header">Status</th><th className="govuk-table__header">Sent</th><th className="govuk-table__header">Actions</th></tr></thead>
        <tbody className="govuk-table__body">{invitations.map(item=><tr className="govuk-table__row" key={item.id}>
          <td className="govuk-table__cell"><strong>{item.invitation_reference}</strong>{item.customer_number&&<small>Customer {item.customer_number}</small>}</td>
          <td className="govuk-table__cell"><strong>{item.recipient_name||'—'}</strong><small>{item.recipient_email}</small></td>
          <td className="govuk-table__cell"><div className="invited-service-tags">{(item.services||[]).map(service=><span key={service.service_code}>{service.service_name}</span>)}</div></td>
          <td className="govuk-table__cell">{purposeLabels[item.purpose]||item.purpose}</td>
          <td className="govuk-table__cell"><span className={'invitation-status '+item.status}>{statusLabels[item.status]||item.status}</span></td>
          <td className="govuk-table__cell">{dateTime(item.last_sent_at||item.sent_at||item.created_at)}</td>
          <td className="govuk-table__cell"><div className="invitation-row-actions">
            <button type="button" className="button secondary small govuk-button govuk-button--secondary" onClick={()=>void act(item,'resend')} disabled={item.status==='cancelled'||item.status==='accepted'||item.status==='expired'||(item.services||[]).some(service=>!services.some(current=>current.code===service.service_code))}><RefreshCw size={14}/> Resend</button>
            <button type="button" className="button danger-outline small govuk-button govuk-button--secondary" onClick={()=>void act(item,'cancel')} disabled={item.status==='cancelled'||item.status==='expired'}><Ban size={14}/> {item.status==='accepted'?'Revoke':'Cancel'}</button>
          </div></td>
        </tr>)}</tbody>
      </table></div>}
    </section>
  </div>;
}
